import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import type { TicketQuery } from '../api/endpoints';
import type { Ticket } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { ErrorMessage } from '../components/ErrorMessage';
import { Icon } from '../components/icons';
import { PageLoader } from '../components/Loader';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { fmtDateTime } from './format';
import { previewText } from './richTextRules';
import { bucketOf, buckets, byUrgency, dueInfo, initials, workAreaOf, workAreas } from './ticketView';
import { assigneeLabel, isOpen, ticketPriorityLabels, ticketStatusLabels } from './tickets';

function useTicketList(q: TicketQuery) {
  const { project } = useProject();
  const papi = useProjectApi();
  return useInfiniteQuery({
    queryKey: projectKeys.tickets(project.id, q),
    queryFn: ({ pageParam, signal }) => papi.tickets({ ...q, cursor: pageParam || undefined, limit: 50 }, signal),
    initialPageParam: '',
    getNextPageParam: (last) => (last.has_more ? (last.next_cursor ?? undefined) : undefined),
  });
}

// TaskCard: ein Vorgang als Karte – was zu tun ist (Betreff, erster Satz), worum es geht (Bezug), wer und bis wann.
// Die ganze Karte ist klickbar (der Betreff-Link spannt sich über die Karte), der Lead-Link bleibt eigenständig.
// Priorität zeigen Leiste und Symbol; als Wort nur „Dringend“.
function TaskCard({ t }: { t: Ticket }) {
  const { project } = useProject();
  const due = dueInfo(t);
  const preview = previewText(t.description);
  const who = assigneeLabel(t);
  const prio = t.priority.toLowerCase();
  return (
    <li className={`task-card prio-${prio}${isOpen(t.status) ? '' : ' is-closed'}`}>
      <span className="task-icon" aria-hidden="true">
        <Icon name={t.kind === 'TASK' ? 'check' : 'message'} size={18} />
      </span>
      <div className="task-main">
        <Link className="task-title" to={`/p/${project.key}/tickets/${t.id}`}>
          {t.subject}
        </Link>
        {preview && <p className="task-preview">{preview}</p>}
        <p className="task-meta">
          <span className="tag-chip">{t.kind === 'TASK' ? 'Aufgabe' : 'Rückfrage'} · {t.category_name ?? t.category}</span>
          {t.lead_id && t.lead_name && (
            <span className="meta-item">
              <Icon name="user" size={14} />
              <Link to={`/p/${project.key}/leads/${t.lead_id}`}>{t.lead_name}</Link>
            </span>
          )}
          {t.appointment_id && (
            <span className="meta-item">
              <Icon name="calendar" size={14} />
              <Link to={`/p/${project.key}/termine/${t.appointment_id}`}>Termin {fmtDateTime(t.appointment_start_at)}</Link>
            </span>
          )}
          {t.comment_count > 0 && (
            <span className="meta-item">
              <Icon name="message" size={14} />
              {t.comment_count === 1 ? '1 Kommentar' : `${t.comment_count} Kommentare`}
            </span>
          )}
          <span className="meta-ref">{t.reference}</span>
        </p>
      </div>
      <div className="task-side">
        {due && (
          <span className={`due due-${due.tone}`}>
            <Icon name="clock" size={14} />
            {due.label}
          </span>
        )}
        <span className="assignee">
          <span className={t.assigned_to_name ? 'avatar' : 'avatar team'} aria-hidden="true">
            {t.assigned_to_name ? initials(t.assigned_to_name) : <Icon name="user" size={12} />}
          </span>
          {who}
        </span>
        {(t.priority === 'URGENT' || t.status !== 'OPEN') && (
          <span className="badges">
            {t.priority === 'URGENT' && <span className="badge badge-error">{ticketPriorityLabels.URGENT}</span>}
            {t.status !== 'OPEN' && <span className={isOpen(t.status) ? 'badge badge-info' : 'badge badge-muted'}>{ticketStatusLabels[t.status]}</span>}
          </span>
        )}
      </div>
    </li>
  );
}

// TicketList: Rückfragen und Aufgaben als Karten. Offene Vorgänge stehen in Gruppen – nach Fälligkeit (überfällig, heute,
// nächste 7 Tage, später, ohne Fälligkeit) bzw. im Posteingang nach Art der Arbeit (groupBy="work", 14.10.2026), dort
// innerhalb des Bereichs nach Dringlichkeit.
export function TicketList({ q, groupBy = 'due' }: { q: TicketQuery; groupBy?: 'due' | 'work' }) {
  const list = useTicketList(q);
  if (list.isPending) return <PageLoader />;
  if (list.isError) return <ErrorMessage error={list.error} onRetry={() => void list.refetch()} />;
  const items = list.data.pages.flatMap((p) => p.items);
  if (!items.length)
    return (
      <EmptyState title="Nichts offen" icon="check">
        Für diese Auswahl gibt es keine Rückfragen oder Aufgaben. Neue Rückfragen legen Sie am Lead oder Termin über „Neue Rückfrage“ an.
      </EmptyState>
    );
  const grouped = items.every((t) => isOpen(t.status));
  const groups = !grouped
    ? [{ key: 'all', label: '', tone: 'muted', items }]
    : groupBy === 'work'
      ? workAreas.map((a) => ({ ...a, items: items.filter((t) => workAreaOf(t) === a.key).sort((x, y) => byUrgency(x, y)) })).filter((g) => g.items.length > 0)
      : buckets.map((b) => ({ ...b, items: items.filter((t) => bucketOf(t) === b.key) })).filter((g) => g.items.length > 0);
  return (
    <>
      <div className="task-groups">
        {groups.map((g) => (
          <section key={g.key} className="task-group" aria-label={g.label || 'Vorgänge'}>
            {g.label && (
              <h3 className={`task-group-title tone-${g.tone}`}>
                {g.label} <span className="task-group-count">{g.items.length}</span>
              </h3>
            )}
            <ul className="task-cards">
              {g.items.map((t) => (
                <TaskCard key={t.id} t={t} />
              ))}
            </ul>
          </section>
        ))}
      </div>
      {list.hasNextPage && (
        <button type="button" className="btn load-more" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
          {list.isFetchingNextPage ? 'Lädt …' : 'Weitere laden'}
        </button>
      )}
    </>
  );
}
